

import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../core/services/user.service';
import { Organization, User } from '../../../core/models/user.model';
import { PermissionService, OrgRole } from '../../../core/services/permission.service';

declare var KTToast: any;

@Component({
      selector: 'app-user-management',
      standalone: true,
      imports: [CommonModule, FormsModule, ReactiveFormsModule],
      templateUrl: './user.component.html'
})
export class UserComponent implements OnInit {
      // Data
      users: User[] = [];
      allOrgs: Organization[] = []; // Load all available Orgs
      allRoles: OrgRole[] = [];     // Master list of roles
      filteredRoles: OrgRole[] = []; // Roles filtered by selected Org

      // State
      userForm: FormGroup;
      isEdit = false;
      isModalOpen = false;

      private userService = inject(UserService);
      private permissionService = inject(PermissionService);
      private fb = inject(FormBuilder);

      constructor() {
            this.userForm = this.fb.group({
                  id: [null],
                  userName: ['', Validators.required],
                  employeeId: ['', Validators.required],
                  orgId: [null, Validators.required],
                  role: [null, Validators.required],
                  email: ['', [Validators.required, Validators.email]],
                  mobileNumber: ['', Validators.required],
                  password: [''], // Will add/remove validator dynamically
                  twofaEnabled: [false],
                  language: ['EN']
            });
      }

      ngOnInit() {
            this.loadInitialData();
            this.loadUsers();
      }

      loadInitialData() {
            // 1. Get All Roles
            this.permissionService.getAllRoles().subscribe(roles => {
                  this.allRoles = roles;

                  // 2. Extract Unique Organizations from Roles (or fetch from OrgService if you have one)
                  const uniqueOrgs = new Map();
                  roles.forEach(r => {
                        if (!uniqueOrgs.has(r.orgId)) {
                              uniqueOrgs.set(r.orgId, { id: r.orgId, name: r.orgName });
                        }
                  });
                  this.allOrgs = Array.from(uniqueOrgs.values());
            });
      }

      loadUsers() {
            this.userService.getUsers(0, 100).subscribe(res => {
                  this.users = res.items;
            });
      }

      // TRIGGERED WHEN ORG CHANGES
      onOrgChange() {
            const selectedOrgId = this.userForm.get('orgId')?.value;
            this.userForm.get('role')?.patchValue(null); // Reset role selection

            if (selectedOrgId) {
                  this.filteredRoles = this.allRoles.filter(r => r.orgId === selectedOrgId);
            } else {
                  this.filteredRoles = [];
            }
      }

      openModal() {
            this.isEdit = false;
            this.isModalOpen = true;
            this.filteredRoles = [];
            this.userForm.reset({ language: 'EN', twofaEnabled: false });

            // Password is required for new users
            this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
            this.userForm.get('password')?.updateValueAndValidity();
      }

      editUser(user: User) {
            this.isEdit = true;
            this.isModalOpen = true;

            // Pre-filter roles so the dropdown shows the correct options immediately
            this.filteredRoles = this.allRoles.filter(r => r.orgId === user.orgId);

            // Password is NOT required when editing (unless you build a change-password feature)
            this.userForm.get('password')?.clearValidators();
            this.userForm.get('password')?.updateValueAndValidity();

            this.userForm.patchValue(user);
      }

      // Add these small details to your existing component


      // Ensure the modal closes on ESC key or background click
      // (Optional logic enhancement)
      closeModal() {
            this.isModalOpen = false;
            this.userForm.reset({ language: 'EN', orgId: null, role: null });
      }

      
      // submit() {
      //       if (this.userForm.invalid) return;

      //       const data = { ...this.userForm.value };

      //       // Safety check: If role is "null" (string) or empty, set to actual null
      //       if (!data.role || data.role === 'null') {
      //             data.role = null;
      //       }

      //       const request = this.isEdit
      //             ? this.userService.update(data.id, data)
      //             : this.userService.create(data);

      //       request.subscribe({
      //             next: () => {
      //                   this.closeModal();
      //                   this.loadUsers();
      //             },
      //             error: (err) => console.error("Create failed", err)
      //       });
      // }
      // 1. Put this at the absolute top of your file, outside of the class!


// ... inside your component class:

submit() {
      if (this.userForm.invalid) return;

      const data = { ...this.userForm.value };

      // Safety check: If role is "null" (string) or empty, set to actual null
      if (!data.role || data.role === 'null') {
            data.role = null;
      }

      const request = this.isEdit
            ? this.userService.update(data.id, data)
            : this.userService.create(data);

      request.subscribe({
            next: (res: any) => {
                  // SUCCESS TOASTER (Fires when backend returns Ok)
                  KTToast.show({
                        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check-circle"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`,
                        message: this.isEdit ? 'User updated successfully!' : 'User logic successful, created successfully!',
                        appearance: 'solid',
                        variant: 'mono', // Metronic Green theme
                        duration: 5000,
                  });

                  this.closeModal();
                  this.loadUsers();
            },
            error: (err) => {
                  // ERROR HANDLING (Extracts "A user with this Email or Username already exists.")
                  let errorMessage = 'This user already exists with this username or email.';
                  
                  if (err.error && typeof err.error === 'string') {
                        errorMessage = err.error; // Catches your custom C# BadRequest text directly
                  } else if (err.error?.message) {
                        errorMessage = err.error.message;
                  }

                  // ERROR TOASTER (Fires when backend returns BadRequest)
                  KTToast.show({
                        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-alert-circle"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
                        message: errorMessage,
                        appearance: 'solid',
                        variant: 'mono', // Metronic Red theme
                        duration: 7000,    // Extra time for users to read the alert
                  });

                  console.error("Operation failed", err);
            }
      });
}
      deleteUser(id: string) {
            if (confirm('Delete user?')) {
                  this.userService.delete(id).subscribe(() => this.loadUsers());
            }
      }
}